/**
 * <TutorLiveCenter> — le centre du live du masteur : l'élève regardé, ses onglets, et ce qu'ils
 * montrent (organism).
 *
 * **Le contenu a la place principale** (Wajih, round 3) : au-dessus de lui, une seule ligne
 * (`<TutorLiveCenterHead>`) — l'élève regardé, les onglets, les gestes —, deux quand le centre est étroit
 * (`ONE_LINE_FROM`), et ce qui alerte le masteur (`alerts`), quand il y en a.
 *
 * Les onglets, soulignés (`<Tabs>`) : Visio (l'élève en grand ; tout le groupe est la mosaïque, un mode
 * de l'écran), puis les vues de l'activité — Tableau blanc et Quiz quand elle les a, Énoncé, Rendu (sa
 * pastille compte les points que la correction a relevés). « Côte à côte » est une bascule au bout de la
 * rangée, là où le centre en a la place (`SPLIT_FROM`). Au téléphone, « Activités » ouvre la rangée.
 * « Contrôles » n'est un onglet que tant qu'on le regarde (`assessments`).
 *
 * Les gestes : l'action qu'attend le statut de l'activité (Valider, Redonner, Soumettre) en un geste,
 * « Afficher à l'élève », « Présenter », et ⋮ pour le reste — dont « Ajouter une activité », venu de la
 * pellicule. Le visage de l'élève ouvre son profil et son brief.
 *
 * « Présenter » partage ce que le centre montre, **sans la ligne de tête** : la région partagée est le
 * contenu seul (`shareRegionRef`, l'ancien recadrage du partage), dans un cadre de l'univers sous le
 * bandeau « Tu présentes » (tous deux hors de la région). Pendant une présentation, le centre n'offre que
 * ce qui se montre au groupe — l'énoncé, le rendu nu, le tableau blanc : ni la visio d'un élève, ni ses
 * contrôles, ni son quiz, ni sa liste ; sans activité à montrer, la région reste vide. L'élève du centre
 * est alors celui de l'activité présentée, quelle que soit la sélection.
 *
 * Le centre seul (`centerOnly`, l'onglet ou l'écran entier partagé, dès le départ du partage) : les
 * élèves voient aussi la tête et les alertes — la tête ne garde que ce qui sert la présentation (le titre de
 * l'activité, la barre de dessin, « Arrêter », les onglets : `presentationOnly`), aucune alerte, et la même
 * règle que pendant une présentation pour le contenu.
 *
 * Pendant une présentation, une ligne au masteur sous la tête (`present.audienceNote`, hors de la région
 * partagée) : les élèves en Concentré ne voient pas la présentation, et « Tous en Dialogue » ; jamais un
 * prénom, puisqu'au centre seul elle part aussi.
 *
 * Contrôlé : l'onglet, le côte à côte et l'activité arrivent en props ; le menu ⋮ ouvert est un état
 * d'interface. Le centre se mesure lui-même.
 */
import React, { useState } from "react"
import { ScrollView, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { daysUntil } from "../../../ports/primary/ui/formatters/dateFormatter"

import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types/schoolAssessment"
import {
    type UITutorLiveAlert,
    type UITutorLiveCenterTab,
    type UITutorLiveFocus,
    type UITutorLiveStudent,
} from "../../../ports/primary/ui/types/tutorLive"
import { type UITutorActivityAction } from "../../../ports/primary/ui/types/tutorEleves"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Icon } from "../../atoms/icons/Icon"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Text } from "../../atoms/typography/Text"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { ActionSheet } from "../../molecules/ActionSheet"
import { Card } from "../../molecules/Card"
import { type TabItem, Tabs } from "../../molecules/Tabs"
import { TutorLiveAlertList } from "../../molecules/TutorLiveAlertList"
import { TutorLiveActivityList } from "../../molecules/TutorLiveActivityList"
import { LIVE_CHOSEN_FACE } from "../../molecules/TutorLiveControlBar"
import { TutorLiveActionBar } from "../../molecules/TutorLiveActionBar"
import {
    type TutorLiveActivityInfo,
    TutorLiveCenterHead,
    type TutorLivePresent,
} from "../../molecules/TutorLiveCenterHead"
import { TutorStudentVideo } from "../../molecules/TutorStudentVideo"
import { UpcomingAssessmentsSection } from "../../templates/UpcomingAssessmentsSection"
import {
    type TutorLiveActivityHandlers,
    TutorLiveActivityView,
    type TutorLiveActivityViewProps,
} from "../TutorLiveActivityView"
import { ACTIVITY_ACTIONS } from "../../molecules/TutorLiveCenterHead/tutorActivityActions"
import { tutorLiveActionBarParts } from "../../molecules/TutorLiveCenterHead/tutorLiveCenterHeadParts"
import { presentationVisual, tutorLiveCenterView, tutorLiveCenterWatched } from "./tutorLiveCenterView"

/** Les gestes sur un élève du live, son id d'abord (ceux de la pellicule). */
export type TutorLiveStudentGestures = {
    onOpenActivity: (studentId: string, activityId: string) => void
    onAddActivity: (studentId: string) => void
    onOpenProfile: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    /** Le prochain contrôle d'un élève : ouvre « Contrôles » au centre. */
    onOpenAssessments?: (studentId: string) => void
    onReorder?: (studentId: string, activityIds: string[]) => void
    onSeeMoreDone?: (studentId: string) => void
    /** L'élève dont la bascule du mode part. */
    modePendingId?: string
}

export type TutorLiveCenterProps = {
    tab: UITutorLiveCenterTab
    onChangeTab: (tab: UITutorLiveCenterTab) => void
    /** Énoncé et rendu côte à côte, quand le centre en a la place. Défaut `false`. */
    split?: boolean
    onToggleSplit?: () => void
    students: UITutorLiveStudent[]
    selectedId?: string
    renderVideo?: (participantId: string) => React.ReactNode
    /** L'activité ouverte, une activité de l'élève regardé. Absente (ou d'un autre élève) : la visio seule. */
    focus?: UITutorLiveFocus | null
    /**
     * Les gestes offerts sur l'activité, ceux de la fiche (`UITutorActivityAction`) : « Afficher à
     * l'élève » en tête, les autres dans ⋮.
     */
    actions?: UITutorActivityAction[]
    /**
     * Sous le titre de l'activité, dans la tête : son chapitre et son chrono (l'ancienne `ActivityItemInfo`).
     * Absente (défaut) : la tête d'aujourd'hui, sans la ligne de l'activité.
     */
    activityInfo?: TutorLiveActivityInfo
    onAction?: (action: UITutorActivityAction) => void
    /** Le geste qui part : son bouton (ou sa ligne) tourne. */
    submitting?: UITutorActivityAction | null
    /**
     * « Affichée chez l'élève ✓ » reste touchable : l'écran répond que l'élève l'a déjà devant lui (l'ancien
     * `ShowToStudentButton`). Défaut `false` : le bouton se grise.
     */
    showPressableWhenShown?: boolean
    /** « Présenter » et la barre de dessin, à toutes les largeurs (le téléphone compris). Absent : pas de bouton. */
    present?: TutorLivePresent
    /** Les gestes de la vue de l'activité : le visuel, les avis, « Signaler ». */
    activityHandlers?: TutorLiveActivityHandlers
    /** « Quiz d'abord · Retirer » : l'élève passe par le quiz avant l'énoncé (la forme de la fiche). */
    quizFirst?: { onRemove: () => void; removing?: boolean } | null
    /** Le calque de dessin de chaque surface (le canevas du moteur). */
    annotation?: TutorLiveActivityViewProps["annotation"]
    /**
     * La présentation partage l'onglet entier (sans recadrage), ou va le partager (le sélecteur est ouvert,
     * l'écran natif part avant que la présentation ne soit marquée) : les élèves voient tout le centre. La
     * tête ne montre plus qui est l'élève regardé (ni visage, ni prénom, ni classe), les alertes se taisent,
     * le contenu ne montre que l'activité. Défaut `false`.
     */
    centerOnly?: boolean
    /** La région que « Présenter » partage : le contenu, sans la ligne de tête (l'ancien recadrage). */
    shareRegionRef?: React.Ref<View>
    /** Les contrôles de l'élève regardé, quand « Contrôles » est ouvert. */
    assessments?: {
        items: UISchoolAssessment[]
        onAdd?: () => void
        onEdit?: (id: UISchoolAssessment["id"]) => void
        onDelete?: (id: UISchoolAssessment["id"]) => void
        onDetails?: (id: UISchoolAssessment["id"]) => void
    }
    /** Ce qui alerte le masteur, au-dessus du centre. */
    alerts?: UITutorLiveAlert[]
    onDismissAlert?: (alertId: string) => void
    /**
     * Le téléphone : l'onglet Activités en tête (la liste de l'élève regardé), sa bascule dans la tête, pas
     * de côte à côte ; « Présenter » reste, comme dans l'app d'aujourd'hui. Défaut `false` : la pellicule
     * porte la liste et la bascule.
     */
    phone?: boolean
    /** Les gestes sur un élève, son id d'abord : le centre les lie à l'élève regardé (au téléphone). */
    gestures: TutorLiveStudentGestures
    testID?: string
}

const TEXTS = {
    tabs: "Ce que montre le centre",
    split: "Côte à côte",
    add: "Ajouter une activité",
    presenting: "Tu présentes · tes élèves voient ce panneau",
    tab: {
        activities: "Activités",
        visio: "Visio",
        whiteboard: "Tableau blanc",
        quiz: "Quiz",
        statement: "Énoncé",
        work: "Rendu",
        assessments: "Contrôles",
    } satisfies Record<UITutorLiveCenterTab, string>,
}

/**
 * Côte à côte à partir de cette largeur de CENTRE. L'ancienne fiche le permettait à 800 px de
 * fenêtre ; ici la pellicule et le fil prennent leur part : le seuil se lit sur le centre, seul juge
 * (avec la pellicule et le fil d'aujourd'hui : oui à 1 280 et 834, non à 1 024).
 */
const SPLIT_FROM = 560
/** Sous cette largeur de centre, les gestes de la tête prennent leur mot court (ils gardent un texte). */
const LABELS_FROM = 900
/**
 * Sous cette largeur de centre, les onglets passent sous les gestes : les gestes ont leur mot (round 4),
 * et l'onglet ouvert ne doit jamais sortir de la rangée.
 */
const ONE_LINE_FROM = 1000
/** L'action qu'attend le statut de l'activité, la première qu'elle offre : elle se garde en un geste. */
/**
 * Les gestes flottent : la tête ne porte plus que l'élève et les onglets, sur une ligne à partir de cette largeur
 * de centre (1 440 px d'écran ; à 1 280 et 1 024, les onglets passent sous l'élève). Plus étroit, l'onglet ouvert
 * sortait de la rangée (« Rendu » caché à 1 280, relevé sur la story).
 */
const ONE_ROW_FROM = 760
const STATUS_ACTIONS: UITutorActivityAction[] = ["validate", "redo", "submit"]
const NO_ACTIONS: UITutorActivityAction[] = []
const NO_ALERTS: UITutorLiveAlert[] = []

export function TutorLiveCenter(props: TutorLiveCenterProps) {
    const {
        tab,
        onChangeTab,
        split = false,
        onToggleSplit,
        students,
        selectedId,
        renderVideo,
        focus,
        actions = NO_ACTIONS,
        activityInfo,
        onAction,
        submitting = null,
        showPressableWhenShown = false,
        present,
        activityHandlers,
        quizFirst = null,
        annotation,
        centerOnly = false,
        shareRegionRef,
        assessments,
        alerts = NO_ALERTS,
        onDismissAlert,
        phone = false,
        gestures,
        testID,
    } = props
    const roomyWidth = useWidthAtLeast(SPLIT_FROM)
    const labelsWidth = useWidthAtLeast(LABELS_FROM)
    const lineWidth = useWidthAtLeast(ONE_LINE_FROM)
    const oneRowWidth = useWidthAtLeast(ONE_ROW_FROM)
    const roomy = roomyWidth.atLeast && !phone
    const [menuOpen, setMenuOpen] = useState(false)

    // Les élèves voient le centre : une présentation tourne ou part, ou l'onglet entier part déjà.
    const watched = tutorLiveCenterWatched({ present, centerOnly })
    const {
        student,
        ownFocus,
        keys,
        current,
        activityTab,
        body: shows,
    } = tutorLiveCenterView({
        students,
        selectedId,
        focus,
        tab,
        phone,
        presenting: watched,
    })
    const corrected = ownFocus?.work?.hasCorrection === true ? (ownFocus.work.correctionPoints?.length ?? 0) : 0
    const items: TabItem<UITutorLiveCenterTab>[] = keys.map((key) => ({
        key,
        label: TEXTS.tab[key],
        count: key === "work" ? corrected : undefined,
    }))
    const canSplit = roomy && ownFocus != null && (current === "statement" || current === "work")
    const showsSplit = canSplit && split

    const statusAction = ownFocus != null ? STATUS_ACTIONS.find((action) => actions.includes(action)) : undefined
    // Les gestes flottent en bas du centre (Wajih, 04/11), sauf au téléphone, où la tête d'avant reste.
    const floating = !phone
    const barView =
        shows === "activity" ? (activityTab != null ? (canSplit && split ? "split" : activityTab) : null) : null
    const bar = tutorLiveActionBarParts({
        view: shows === "activity" ? barView : shows === "nothing" ? null : shows,
        focus: ownFocus != null,
        status: ownFocus?.status != null,
        primary: statusAction != null,
        show: actions.includes("show"),
        present: present != null,
        watched,
        add: student != null,
    })
    // Pendant une présentation, la barre de dessin prend la tête : l'action du statut repasse dans ⋮ (l'ancienne
    // fiche gardait Soumettre, Valider et Redonner pendant la présentation, C-113). La barre flottante la garde en un
    // geste sur le rendu seulement : ailleurs elle est dans « Plus ».
    const headHoldsStatus = floating ? bar.primary : !(present?.on === true && present.annotation != null)
    const menuItems = [
        ...(ownFocus != null ? actions : [])
            .filter((action) => action !== "show" && (action !== statusAction || !headHoldsStatus))
            .map((action) => ({
                icon: ACTIVITY_ACTIONS[action].icon,
                label: ACTIVITY_ACTIONS[action].label,
                onPress: () => onAction?.(action),
            })),
        ...(student != null && !phone
            ? [{ icon: "plus" as const, label: TEXTS.add, onPress: () => gestures.onAddActivity(student.id) }]
            : []),
    ]

    const presenting = present?.on === true && activityTab != null
    const activityView = activityTab != null ? (showsSplit ? "split" : activityTab) : null
    // Le visuel du tableau blanc passe dans la barre de dessin pendant la présentation (hors de la région).
    const visual = presentationVisual({
        presenting: present?.on === true,
        view: activityView,
        visual: ownFocus?.visual,
        onToggle: activityHandlers?.onToggleVisual,
        onFeedback: activityHandlers?.onVisualFeedback,
    })
    let body: React.ReactNode
    if (shows === "activities" && student != null) {
        body = (
            <ScrollView contentContainerStyle={styles.phoneList}>
                <Card p="2">
                    <TutorLiveActivityList
                        key={student.id}
                        student={student}
                        onReorder={
                            gestures.onReorder != null ? (ids) => gestures.onReorder?.(student.id, ids) : undefined
                        }
                        onSeeMoreDone={
                            gestures.onSeeMoreDone != null ? () => gestures.onSeeMoreDone?.(student.id) : undefined
                        }
                        moreDone={student.moreDone}
                        activities={student.activities}
                        openActivityId={ownFocus?.activityId}
                        onOpenActivity={(activityId) => gestures.onOpenActivity(student.id, activityId)}
                        onAddActivity={() => gestures.onAddActivity(student.id)}
                        onOpenProfile={() => gestures.onOpenProfile(student.id)}
                        size="lg"
                        defaultDoneOpen
                    />
                </Card>
            </ScrollView>
        )
    } else if (shows === "assessments" && student != null) {
        body = (
            <Card p="3">
                <UpcomingAssessmentsSection
                    assessments={assessments?.items ?? []}
                    onAddAssessment={assessments?.onAdd}
                    onEditAssessment={assessments?.onEdit}
                    onDeleteAssessment={assessments?.onDelete}
                    onAssessmentDetails={assessments?.onDetails}
                />
            </Card>
        )
    } else if (shows === "visio" && student != null) {
        // La visio : l'élève regardé, en grand (tout le groupe est la mosaïque, un mode de l'écran).
        body = (
            <View style={styles.stage}>
                <TutorStudentVideo student={student} size="stage" video={renderVideo?.(student.id)} />
            </View>
        )
    } else if (shows !== "activity" || activityTab == null || ownFocus == null) {
        // Rien à montrer (une présentation sans activité) : la région reste vide, jamais un élève.
        body = null
    } else {
        body = (
            <TutorLiveActivityView
                focus={ownFocus}
                view={showsSplit ? "split" : activityTab}
                handlers={activityHandlers}
                presenting={watched}
                roomy={roomy}
                annotation={annotation}
            />
        )
    }

    return (
        <View
            style={styles.root}
            onLayout={(event) => {
                roomyWidth.onLayout(event)
                labelsWidth.onLayout(event)
                lineWidth.onLayout(event)
                oneRowWidth.onLayout(event)
            }}
            testID={testID}
        >
            {/* L'onglet entier part : une alerte peut nommer un élève, elle se tait. */}
            <TutorLiveAlertList alerts={centerOnly ? NO_ALERTS : alerts} onDismiss={onDismissAlert} />
            <TutorLiveCenterHead
                floating={floating}
                student={student}
                focus={ownFocus}
                primary={
                    statusAction != null
                        ? {
                              action: statusAction,
                              onPress: () => onAction?.(statusAction),
                              pending: submitting === statusAction,
                          }
                        : undefined
                }
                show={
                    actions.includes("show")
                        ? {
                              onPress: () => onAction?.("show"),
                              pending: submitting === "show",
                              pressableWhenShown: showPressableWhenShown,
                          }
                        : undefined
                }
                present={present}
                onOpenMenu={menuItems.length > 0 ? () => setMenuOpen(true) : undefined}
                mode={
                    phone && student != null
                        ? {
                              onChange: (mode) => gestures.onChangeMode(student.id, mode),
                              pending: student.id === gestures.modePendingId,
                          }
                        : undefined
                }
                onOpenProfile={student != null ? () => gestures.onOpenProfile(student.id) : undefined}
                objective={
                    student != null
                        ? {
                              subjects: student.objective?.subjects ?? [],
                              label: student.objective?.label,
                              assessment:
                                  student.nextAssessment != null && gestures.onOpenAssessments != null
                                      ? {
                                            daysUntil: daysUntil(student.nextAssessment.date),
                                            open: current === "assessments",
                                            onPress: () => gestures.onOpenAssessments?.(student.id),
                                        }
                                      : undefined,
                          }
                        : undefined
                }
                quizFirst={quizFirst}
                status={!phone && labelsWidth.atLeast && ownFocus?.status != null ? ownFocus.status : undefined}
                activityInfo={ownFocus != null ? activityInfo : undefined}
                compact={!labelsWidth.atLeast}
                // Dès le départ du partage (le natif diffuse avant que la présentation ne soit marquée).
                hideIdentity={centerOnly}
                // L'écran entier part : la tête ne garde que ce qui sert la présentation (tech-derisk, point 3 B).
                presentationOnly={centerOnly}
                visual={visual}
                oneLine={(floating ? oneRowWidth.atLeast : lineWidth.atLeast) && !phone}
                tabs={
                    <Tabs
                        items={items}
                        value={current}
                        onChange={onChangeTab}
                        size="sm"
                        accessibilityLabel={TEXTS.tabs}
                        trailing={
                            canSplit && onToggleSplit != null ? (
                                <IconButton
                                    iconName="columns"
                                    shape="square"
                                    size={32}
                                    iconSize={18}
                                    {...(split
                                        ? LIVE_CHOSEN_FACE
                                        : { appearance: "transparent", iconColor: "text-strong" })}
                                    selected={split}
                                    onPress={onToggleSplit}
                                    accessibilityLabel={TEXTS.split}
                                />
                            ) : undefined
                        }
                    />
                }
            />
            {/* Au masteur, hors de la région partagée : ce que son public ne voit pas. */}
            {present?.on === true && present.audienceNote != null && (
                <View style={styles.note}>
                    <Icon name="eye-slash" size={16} color="text-muted" />
                    <Text size="text-sm" color="text-muted" style={styles.noteText}>
                        {present.audienceNote.text}
                    </Text>
                    {present.audienceNote.action != null && (
                        <Button
                            label={present.audienceNote.action.label}
                            iconName="microphone"
                            variant="ghost"
                            size="sm"
                            onPress={present.audienceNote.action.onPress}
                        />
                    )}
                </View>
            )}
            {/* Un seul arbre, présentation ou pas : « Présenter » ne remonte pas la vue (ni ses images, ni son
                défilement) ; le cadre et le bandeau s'y ajoutent. */}
            {/* La barre flottante a sa place réservée sous le contenu : la fin d'un rendu ne passe jamais dessous. */}
            <View style={[styles.body(presenting), floating && bar.shown && styles.underBar]}>
                {presenting && (
                    <View style={styles.ribbon}>
                        <NotificationDot color="text-on-role" size={8} />
                        <Text size="text-sm" weight="bold" color="text-on-role">
                            {TEXTS.presenting}
                        </Text>
                    </View>
                )}
                {/* La région partagée : le contenu seul, sans le bandeau ni le cadre. L'espace est AUTOUR d'elle, et
                    elle porte son propre arrondi : collée au bord intérieur du cadre, un rectangle débordait sur la
                    courbe de ses coins (le teal entrait dans la région, un coin carré dépassait de l'arrondi). */}
                <View style={styles.well(presenting)}>
                    <View style={styles.inner(presenting)} ref={shareRegionRef} collapsable={false}>
                        {body}
                    </View>
                </View>
            </View>
            {floating && bar.shown && (
                // La barre flotte au-dessus du contenu, en bas au centre ; ce qui est autour d'elle reste touchable.
                <View style={styles.floating} pointerEvents="box-none">
                    <TutorLiveActionBar
                        info={
                            bar.info && ownFocus != null
                                ? { title: ownFocus.activityTitle, chrono: activityInfo?.chrono }
                                : undefined
                        }
                        status={bar.status ? ownFocus?.status : undefined}
                        primary={
                            bar.primary && statusAction != null
                                ? {
                                      action: statusAction,
                                      onPress: () => onAction?.(statusAction),
                                      pending: submitting === statusAction,
                                  }
                                : undefined
                        }
                        show={
                            bar.show
                                ? {
                                      onPress: () => onAction?.("show"),
                                      pending: submitting === "show",
                                      shown: ownFocus?.shownToStudent === true,
                                      pressableWhenShown: showPressableWhenShown,
                                  }
                                : undefined
                        }
                        present={
                            bar.present && present != null
                                ? { onPress: present.onToggle, pending: present.pending }
                                : undefined
                        }
                        onAdd={bar.add && student != null ? () => gestures.onAddActivity(student.id) : undefined}
                        onOpenMenu={bar.menu && menuItems.length > 0 ? () => setMenuOpen(true) : undefined}
                        compact={!labelsWidth.atLeast}
                    />
                </View>
            )}
            <ActionSheet
                visible={menuOpen}
                onClose={() => setMenuOpen(false)}
                title={ownFocus?.activityTitle}
                items={menuItems}
            />
        </View>
    )
}

const PRESENT_FRAME = 3

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, minWidth: 0, gap: theme.spacing["2"] },
    underBar: { marginBottom: theme.spacing["16"] },
    /** La barre flottante : en bas au centre, au-dessus du contenu, à distance de la barre du live. */
    floating: {
        position: "absolute" as const,
        left: theme.spacing["2"],
        right: theme.spacing["2"],
        bottom: theme.spacing["1"],
        alignItems: "center" as const,
        zIndex: 2,
    },
    stage: { flex: 1, minHeight: 0, borderRadius: theme.radius.lg, overflow: "hidden" as const },
    phoneList: { gap: theme.spacing["3"], paddingBottom: theme.spacing["4"] },
    /** Ce que voient les élèves, pendant une présentation : le cadre de l'univers, son bandeau en tête. */
    body: (presenting: boolean) => ({
        flex: 1,
        minHeight: 0,
        ...(presenting
            ? {
                  borderRadius: theme.radius.lg,
                  borderWidth: PRESENT_FRAME,
                  borderColor: theme.colors["role-fill"],
                  overflow: "hidden" as const,
              }
            : {}),
    }),
    ribbon: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["2"],
        backgroundColor: theme.colors["role-fill"],
    },
    /** La ligne du masteur pendant une présentation : discrète, elle passe à la ligne plutôt que de couper. */
    note: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
    },
    noteText: { flexShrink: 1, minWidth: 0 },
    well: (presenting: boolean) => ({ flex: 1, minHeight: 0, padding: presenting ? theme.spacing["2"] : 0 }),
    inner: (presenting: boolean) => ({
        flex: 1,
        minHeight: 0,
        ...(presenting ? { borderRadius: theme.radius.md, overflow: "hidden" as const } : {}),
    }),
}))
